/**
 * Collects the disabling conditions for the Documents tab "Trigger RPA"
 * button into a human-readable message list.
 *
 * The page already receives the blocker booleans; this hook only maps them
 * to copy so the disabled state and the button tooltip always agree.
 *
 * @module useDocumentRpaValidationMessages
 */
import { useMemo } from "react";
import { DocumentRpaValidationMessages } from "../../../../../constants/documentRpaValidationMessages";

/**
 * The individual RPA blockers, as computed by {@link useDocumentRpaActions}.
 */
export interface DocumentRpaBlockers {
  /** A document RPA run is already in progress. */
  isRpaInProgress: boolean;
  /** A prior document write-back is in progress or queued. */
  isDocumentWriteBackInProgress: boolean;
  /** No J-codes are present on the order. */
  hasNoJCodes: boolean;
}

interface UseDocumentRpaValidationMessagesParams {
  /** The individual RPA blockers. */
  rpaBlockers: DocumentRpaBlockers;
  /** Any CPT row has an invalid value on a save-blocking field. */
  hasCptTableAnyError: boolean;
  /** The org's health-care-facility data is still loading. */
  isFacilityDataLoading: boolean;
}

export interface DocumentRpaValidationMessagesResult {
  /** One message per failing validation, in stable display order. */
  validationMessages: string[];
  /** True when the "Trigger RPA" button should be disabled. */
  isTriggerRpaDisabled: boolean;
}

/**
 * Maps the Documents tab Trigger RPA blockers to tooltip messages.
 *
 * @returns The failing validations as messages plus the combined disabled flag.
 */
export const useDocumentRpaValidationMessages = ({
  rpaBlockers,
  hasCptTableAnyError,
  isFacilityDataLoading,
}: UseDocumentRpaValidationMessagesParams): DocumentRpaValidationMessagesResult => {
  const { isRpaInProgress, isDocumentWriteBackInProgress, hasNoJCodes } =
    rpaBlockers;

  const validationMessages = useMemo(() => {
    const messages: string[] = [];
    if (isRpaInProgress) {
      messages.push(DocumentRpaValidationMessages.RPA_IN_PROGRESS);
    }
    if (isDocumentWriteBackInProgress) {
      messages.push(
        DocumentRpaValidationMessages.DOCUMENT_WRITE_BACK_IN_PROGRESS,
      );
    }
    if (hasNoJCodes) {
      messages.push(DocumentRpaValidationMessages.NO_J_CODES);
    }
    if (hasCptTableAnyError) {
      messages.push(DocumentRpaValidationMessages.CPT_TABLE_HAS_ERRORS);
    }
    if (isFacilityDataLoading) {
      messages.push(DocumentRpaValidationMessages.FACILITY_DATA_LOADING);
    }
    return messages;
  }, [
    isRpaInProgress,
    isDocumentWriteBackInProgress,
    hasNoJCodes,
    hasCptTableAnyError,
    isFacilityDataLoading,
  ]);

  return {
    validationMessages,
    isTriggerRpaDisabled: validationMessages.length > 0,
  };
};
